import * as SecureStore from "expo-secure-store";

import type {
  AppUserSession,
  NullableAppUserSession,
} from "@/schemas/auth/session";
import AuthService from "@/services/internal/auth";
import { parseAsUTC, utcNow } from "@/utils/datetime";

export function isSessionExpired(
  session: AppUserSession,
  hoursBuffer = 1,
): boolean {
  const expiryDatetime = parseAsUTC(session.token.expiresAt).getTime();
  const currentTime = utcNow().getTime();
  const bufferMs = hoursBuffer * 60 * 60 * 1000; // Convert hours to milliseconds
  // Return true if current time + buffer is greater than or equal to expiry time
  return currentTime + bufferMs >= expiryDatetime;
}

export async function getOrRefreshSessionFromSecureStore(
  currentSession: AppUserSession,
): Promise<AppUserSession> {
  const sessionString = await SecureStore.getItemAsync("session");
  const localStorageSession = parseSessionString(sessionString);

  if (localStorageSession !== null) {
    if (!isSessionExpired(localStorageSession)) {
      return localStorageSession;
    }
  }

  const newSession = await refreshSession(currentSession);
  await SecureStore.setItemAsync("session", JSON.stringify(newSession));
  return newSession;
}

export function parseSessionString(
  sessionString: string | null | undefined,
): NullableAppUserSession {
  if (sessionString === null || sessionString === undefined) {
    return null;
  }
  const session: AppUserSession = JSON.parse(sessionString);
  return session;
}

async function refreshSession(
  session: AppUserSession,
): Promise<AppUserSession> {
  const authService = new AuthService();
  return await authService.refreshToken({
    username: session.identity.username,
    refreshToken: session.token.refreshToken,
  });
}
